import React, { useState, useEffect } from "react";
import ToolShell from "./ToolShell";
import { Coins, RefreshCw } from "lucide-react";
import { fetchFX } from "../../lib/api";
import { useLocale } from "../../i18n";

const PAIRS = ["RON", "EUR", "USD", "GBP", "HUF", "PLN", "CHF"];

export default function CurrencyTool() {
  const { t, locale } = useLocale();
  const [base, setBase] = useState("RON");
  const [target, setTarget] = useState("EUR");
  const [amount, setAmount] = useState(100);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const data = await fetchFX(base, target, Number(amount) || 1);
      setResult(data);
    } catch (e) { setResult({ error: true }); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const swap = () => { setBase(target); setTarget(base); };

  return (
    <ToolShell
      title={t("tools.currency")}
      subtitle={t("tools.currency_sub")}
      icon={Coins}
      testid="tool-currency"
      seoPath="/tools/currency"
      seoTitle={locale === "ro" ? "Convertor RON — curs valutar live" : "RON currency converter — live exchange rate"}
      seoDescription={locale === "ro" ? "Convertor valutar RON cu curs live. Convertește orice pereche de valute pentru călătoria ta în România." : "RON currency converter with live exchange rates. Convert any currency pair for your Romania trip."}
    >
      <div className="grid lg:grid-cols-12 gap-6">
        <form
          onSubmit={(e) => { e.preventDefault(); run(); }}
          className="lg:col-span-5 wr-sharp-card p-8 space-y-6"
          data-testid="currency-form"
        >
          <div>
            <label className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] block mb-2">{t("common.amount")}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full font-mono-wr text-3xl bg-transparent border-0 border-b border-[var(--wr-border)] focus:border-[var(--wr-ink)] py-2 focus:outline-none transition-colors"
              data-testid="currency-amount"
            />
          </div>
          <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
            <div>
              <label className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] block mb-2">{t("common.from")}</label>
              <select value={base} onChange={(e) => setBase(e.target.value)} className="w-full font-mono-wr text-lg border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2 focus:border-[var(--wr-ink)] focus:outline-none" data-testid="currency-base">
                {PAIRS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <button type="button" onClick={swap} className="mb-1 p-2 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] transition-colors" data-testid="currency-swap" aria-label="swap">
              <RefreshCw size={16} />
            </button>
            <div>
              <label className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] block mb-2">{t("common.to")}</label>
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="w-full font-mono-wr text-lg border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2 focus:border-[var(--wr-ink)] focus:outline-none" data-testid="currency-target">
                {PAIRS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-[var(--wr-ink)] text-[var(--wr-bg)] font-mono-wr text-[11px] uppercase tracking-widest hover:bg-[var(--wr-red)] transition-colors disabled:opacity-50"
            data-testid="currency-submit"
          >
            {loading ? t("common.loading") : t("common.calculate")}
          </button>
        </form>

        <div className="lg:col-span-7 wr-sharp-card p-8 lg:p-12 bg-[var(--wr-night)] text-[var(--wr-bg)] flex flex-col justify-between min-h-[360px]" data-testid="currency-result">
          <div className="flex items-center justify-between">
            <p className="font-mono-wr text-[10px] uppercase tracking-widest text-white/75">{t("common.result")}</p>
            {result && (
              <span className={`font-mono-wr text-[10px] uppercase tracking-widest px-2 py-1 border ${result.live ? "border-[var(--wr-red)] text-[var(--wr-red)]" : "border-white/50 text-white/85"}`}>
                {result.live ? t("common.live") : t("common.fallback")}
              </span>
            )}
          </div>
          {result && !result.error && (
            <div className="mt-4">
              <p className="font-mono-wr text-white/80 text-sm">
                {result.amount} {result.base} =
              </p>
              <p className="font-editorial text-[64px] lg:text-[96px] leading-none tracking-tight mt-4 break-all">
                {Number(result.converted).toLocaleString(locale === "ro" ? "ro-RO" : "en-US", { maximumFractionDigits: 2 })}
                <span className="text-[var(--wr-red)] ml-3 text-4xl lg:text-5xl">{result.target}</span>
              </p>
              <p className="font-mono-wr text-white/75 text-xs mt-6">
                1 {result.base} = {Number(result.rate).toFixed(4)} {result.target} · {result.source}
                {result.as_of ? ` · ${result.as_of}` : ""}
              </p>
            </div>
          )}
        </div>
      </div>
    </ToolShell>
  );
}
